import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import DownloadIcon from '@mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useMemo, useState } from 'react'
import { type NestleSample, useGetNestleSamplesQuery } from '../../store/api/qualityApi'
import { useAppSelector } from '../../store'
import { API_BASE_URL } from '../../config/api'
import BrandState from '../../components/BrandState'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import {
  DataframeBody,
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useLocalDatasheetColumns } from '../../utils/datasheetColumns/useLocalDatasheetColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'
import { useSnackbar } from '../../hooks/useSnackbar'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'

/** btnEx guard: Access "Select not over 20 rows" — export enabled for 1..20 selected. */
const MAX_EXPORT_ROWS = 20
const ALL_ROWS_PAGE_SIZE = 1_000_000
/** Per-tab export token (same value Access btnEx_Click embeds in the legacy URL). */
const EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

type ColumnKind = 'text' | 'date' | 'number'
type ColumnDef = {
  key: keyof NestleSample
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
}

/**
 * Columns follow Access `frmQualityQrySamplingExp` (ColumnOrder + Label.Caption).
 * `id` is shown between Shipping line and Booking # (per ColumnOrder); `chk` is the
 * FE "Sel" column.
 * Numeric: Lot size / Weight (Standard, 2dp right). `col9` is the shipping-month date.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'col1', label: 'SL/LD' },
  { key: 'col2', label: 'PS date/LD date' },
  { key: 'col3', label: 'SL date/LD time' },
  { key: 'col4', label: 'Warehouse', filter: 'checkboxes' },
  { key: 'col5', label: 'Trader', filter: 'checkboxes' },
  { key: 'col6', label: 'Exporter', filter: 'checkboxes' },
  { key: 'col7', label: 'Origin', filter: 'checkboxes' },
  { key: 'col8', label: 'Contract #' },
  { key: 'col9', label: 'Shipping month' },
  { key: 'col10', label: 'SPEC.' },
  { key: 'col11', label: 'Nestle Market' },
  { key: 'col12', label: 'Lot Number' },
  { key: 'col13', label: 'Lot size(MT)', kind: 'number', align: 'right' },
  { key: 'col14', label: 'Weight/cont. (MT)', kind: 'number', align: 'right' },
  { key: 'col15', label: 'Sust Pro.' },
  { key: 'col24', label: 'EURD' },
  { key: 'col16', label: 'License #4C RFA UTZ' },
  { key: 'col17', label: 'Unit Name 4C RFA UTZ' },
  { key: 'col18', label: 'Loading Type', filter: 'checkboxes' },
  { key: 'col19', label: 'Jute bag Name' },
  { key: 'col20', label: 'Seal #' },
  { key: 'col21', label: 'Shipping line' },
  { key: 'id', label: 'Id' },
  { key: 'col22', label: 'Booking #' },
  { key: 'col25', label: 'WH location' },
  { key: 'col23', label: 'Remark' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Date `d-mmm-yyyy`; falls back to the raw value when not an ISO date. */
const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const s = String(value)
  if (!/^\d{4}-\d{2}-\d{2}/.test(s)) return s
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return s
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessNumber = (value: unknown): string => {
  const n = typeof value === 'number' ? value : Number(value)
  if (!Number.isFinite(n)) return value == null ? '' : String(value)
  if (Number.isInteger(n)) return n.toLocaleString('en-US')
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const cellValue = (row: NestleSample, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'number') return formatAccessNumber(val)
  return String(val)
}

interface NestleSampleListViewProps {
  startDate?: string
  endDate?: string
}

export default function NestleSampleListView({ startDate = '', endDate = '' }: NestleSampleListViewProps) {
  const snack = useSnackbar()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')

  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const [exporting, setExporting] = useState(false)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof NestleSample> | null>(null)

  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: TABLE_COLUMNS,
    storageKey: 'quality.nestle-sample.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths('quality.nestle-sample.list')

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof NestleSample
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetNestleSamplesQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(startDate ? { startDate } : {}),
      ...(endDate ? { endDate } : {}),
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }

  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const toggleSort = (key: keyof NestleSample) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const toggleRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectedCount = selectedIds.size
  const canExport = selectedCount >= 1 && selectedCount <= MAX_EXPORT_ROWS && !!email && !exporting

  // Access btnEx_Click: export the checked rows (≤20) via the legacy NestleSampling
  // template route; lst_id is the comma-joined id list.
  const handleExport = async () => {
    if (selectedCount === 0) {
      snack.warning('Please select at least one row.')
      return
    }
    if (selectedCount > MAX_EXPORT_ROWS) {
      snack.warning(`Select not over ${MAX_EXPORT_ROWS} rows.`)
      return
    }
    setExporting(true)
    try {
      const lstId = Array.from(selectedIds).join(',')
      const url = `${API_BASE_URL}/erp-legacy/download/quality/nestlesample?token=${encodeURIComponent(EXPORT_TOKEN)}&email=${encodeURIComponent(email)}&lst_id=${encodeURIComponent(lstId)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = href
      a.download = 'NestleSampling.xlsx'
      a.click()
      URL.revokeObjectURL(href)
    } catch {
      snack.error('Export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const minTableWidth = Math.max(1200, (visibleColumns.length + 1) * 96)

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={!canExport}
            onClick={() => void handleExport()}
            startIcon={exporting ? <CircularProgress size={13} color="inherit" /> : <DownloadIcon sx={{ fontSize: 16 }} />}
            title={selectedCount > MAX_EXPORT_ROWS ? `Select not over ${MAX_EXPORT_ROWS} rows` : 'Export selected'}
            sx={{ minWidth: 0, px: 1, py: 0.2, minHeight: 26, fontSize: '0.72rem', textTransform: 'none', whiteSpace: 'nowrap' }}
          >
            {/* Pattern 10: discriminator (count) luôn show; ẩn prefix "Export (All)"
                trên xs+sm — icon + "( N )" đủ phân biệt, title prop làm tooltip. */}
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export&nbsp;(All)</Box>( {selectedCount} )
          </Button>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e: MouseEvent<HTMLElement>) => filterPopover.open('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Nestle Sample data.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Selected: {selectedCount}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={() => filterPopover.close()}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Contract, lot, warehouse..."
              label="Search Nestle samples"
              onClear={() => filterPopover.close()}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.label ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <DataframeBody loading={isLoading} fetching={isFetching} sx={{ minHeight: { xs: 280, sm: 320 } }}>
          {error ? (
            <BrandState tone="error" title="Could not load Nestle Sample data" description="Please refresh the page or contact the system administrator if the issue continues." />
          ) : isLoading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState title="No Nestle Sample found" description="Try adjusting the date range or the table filters above." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  <TableCell
                    className="app-table-header-cell"
                    padding="checkbox"
                    style={{ position: 'sticky', top: 0, left: 0, zIndex: 100, width: 44, minWidth: 44, maxWidth: 44 }}
                  >
                    Sel
                  </TableCell>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const w = getWidth(key)
                    const headerStyle: CSSProperties = { position: 'relative' }
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    return (
                      <TableCell
                        key={key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={headerStyle}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text' }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: NestleSample) => {
                const checked = selectedIds.has(row.id)
                return (
                  <>
                    <TableCell
                      padding="checkbox"
                      className="app-sticky-col"
                      style={{ width: 44, minWidth: 44, maxWidth: 44 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        size="small"
                        checked={checked}
                        onChange={() => toggleRow(row.id)}
                        sx={{ p: 0.25 }}
                        inputProps={{ 'aria-label': `Select row ${row.id}` }}
                      />
                    </TableCell>
                    {visibleColumns.map((col) => {
                      const key = String(col.key)
                      const w = getWidth(key)
                      const wrap = getWidth(key) != null
                      const val = cellValue(row, col)
                      if (wrap) {
                        return (
                          <TableCell
                            key={key}
                            align={col.align}
                            style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                            sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                          >
                            <Box component="span" className="app-table-cell-wrap">{val || '-'}</Box>
                          </TableCell>
                        )
                      }
                      return (
                        <TableCell
                          key={key}
                          align={col.align}
                          sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                        >
                          <Box component="span" className="app-table-cell-text" title={val || '-'}>
                            {val || '-'}
                          </Box>
                        </TableCell>
                      )
                    })}
                  </>
                )
              }}
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && selectedIds.has(rowId)}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => { if (rowId != null) toggleRow(rowId) }}
                    />
                  )
                },
              }}
            />
          )}
        </DataframeBody>
      </Box>

      <FuturesColumnsMenu controller={columnsCtrl} />
    </Box>
  )
}
